import * as React from "react";

import { cn } from "@athena/ui/lib/utils";

const Textarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<"textarea">>(
    ({ className, ...props }, ref) => {
        return (
            <textarea
                className={cn(
                    "border-foreground/20 placeholder:text-muted-foreground/50 focus-visible:ring-ring bg-card flex min-h-20 w-full rounded-lg border px-4 py-2 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                    className,
                )}
                ref={ref}
                {...props}
            />
        );
    },
);
Textarea.displayName = "Textarea";

/**
 * A textarea that grows to fit what it holds, so long text wraps instead of scrolling out of view.
 *
 * Height is set from `scrollHeight` on every value change, the same idiom as the title field in
 * ContentEditor. `resize-none` removes the drag handle, since the box now sizes itself.
 *
 * Growth has to stop somewhere for a field that shares its pane with something else — a composer
 * sits above the conversation it is writing into, and one that grew without limit would push the
 * thread off the screen. Pass a `max-h-*` class to set that ceiling; the field then scrolls from
 * there on, and without one it simply keeps growing.
 */
function AutoGrowTextarea({ value, className, ...props }: React.ComponentProps<typeof Textarea>) {
    const ref = React.useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
        /**
         * Only once a `max-h-*` ceiling has taken over, which is the one state where the box is
         * shorter than its text. Below the ceiling the two match and a scrollbar would be a
         * permanent artifact of measuring, not something the reader can use.
         */
        el.style.overflowY = el.scrollHeight > el.clientHeight ? "auto" : "hidden";
    }, [value]);

    return (
        <Textarea
            ref={ref}
            value={value}
            rows={1}
            className={cn("resize-none overflow-hidden", className)}
            {...props}
        />
    );
}

export { Textarea, AutoGrowTextarea };
